import React from "react";
import { useTranslation } from "react-i18next";
import { THERAPY_TYPES } from "../../../../shared/domain/therapyTypes";
import {
    MarketplaceFilters,
    PriceModel,
    TOOL_CATEGORIES,
    TherapyType,
    ToolCategory,
} from "../../domain/types";

interface ToolFiltersProps {
    filters: MarketplaceFilters;
    onChange: (filters: MarketplaceFilters) => void;
}

const selectClasses =
    "h-11 min-h-[44px] rounded-lg border border-gray-200 bg-white px-3 text-sm text-gray-700 focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300";

const ToolFilters: React.FC<ToolFiltersProps> = ({ filters, onChange }) => {
    const { t } = useTranslation();

    return (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Labels first, matching the order the chips appear on a card. */}
            <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                {t("marketplace.filterCategory")}
                <select
                    aria-label={t("marketplace.filterCategory")}
                    className={selectClasses}
                    value={filters.category ?? ""}
                    onChange={(e) =>
                        onChange({
                            ...filters,
                            category: (e.target.value || undefined) as ToolCategory | undefined,
                        })
                    }
                >
                    <option value="">{t("marketplace.allCategories")}</option>
                    {TOOL_CATEGORIES.map((category) => (
                        <option key={category} value={category}>
                            {t(`marketplace.categories.${category}`)}
                        </option>
                    ))}
                </select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                {t("marketplace.filterTherapyType")}
                <select
                    aria-label={t("marketplace.filterTherapyType")}
                    className={selectClasses}
                    value={filters.therapyType ?? ""}
                    onChange={(e) =>
                        onChange({
                            ...filters,
                            therapyType: (e.target.value || undefined) as TherapyType | undefined,
                        })
                    }
                >
                    <option value="">{t("marketplace.allTypes")}</option>
                    {THERAPY_TYPES.map((type) => (
                        <option key={type} value={type}>
                            {t(`session.therapyOptions.${type}`)}
                        </option>
                    ))}
                </select>
            </label>

            <label className="flex flex-col gap-1 text-xs font-medium text-content-tertiary">
                {t("marketplace.filterPrice")}
                <select
                    aria-label={t("marketplace.filterPrice")}
                    className={selectClasses}
                    value={filters.priceModel ?? ""}
                    onChange={(e) =>
                        onChange({
                            ...filters,
                            priceModel: (e.target.value || undefined) as PriceModel | undefined,
                        })
                    }
                >
                    <option value="">{t("marketplace.allPrices")}</option>
                    <option value={PriceModel.FREE}>{t("marketplace.priceFree")}</option>
                    <option value={PriceModel.SUBSCRIPTION}>{t("marketplace.priceSubscription")}</option>
                </select>
            </label>
        </div>
    );
};

export default ToolFilters;
